import * as Comlink from 'comlink';
import { RECEIVE_PORT_MESSAGE_TYPE, REQUEST_PORT_MESSAGE_TYPE } from './const';

export { requestMessagePortFromHost } from './requestPort';
export type { Remote, Endpoint } from 'comlink';

type RequestApiEvent = {
  type: typeof REQUEST_PORT_MESSAGE_TYPE;
};

type SDKFactory = (...args: any[]) => any;
type CreateSDKFactory = (
  event: MessageEvent<RequestApiEvent>,
) => SDKFactory | null | undefined;

function isRequestPortMessage(event: MessageEvent) {
  return event.data?.type === REQUEST_PORT_MESSAGE_TYPE;
}

function createReceivePortMessage(): {
  type: typeof RECEIVE_PORT_MESSAGE_TYPE;
} {
  return {
    type: RECEIVE_PORT_MESSAGE_TYPE,
  };
}

// The initial purpose of this method is to handle requested from iframes,
// while in fact it could handle all incoming messages on a "window"
export async function onApiRequestedFromEventSource(
  onCreateSDKFactory: CreateSDKFactory,
) {
  if (typeof window === 'undefined') {
    throw new Error(
      'Unable to subscribe on API request from environment with no access to the window',
    );
  }

  function handleMessage(event: MessageEvent) {
    if (!isRequestPortMessage(event)) {
      return;
    }

    if (!event.source) {
      throw new Error('Unable to find event.source');
    }

    const sdkFactory = onCreateSDKFactory(
      event as MessageEvent<RequestApiEvent>,
    );

    if (!sdkFactory) {
      return;
    }

    const { port1, port2 } = new MessageChannel();
    Comlink.expose(
      // We need to wrap an instance of the SDK with a proxy object
      (...args: unknown[]) => {
        const sdk = sdkFactory(...args);
        return Comlink.proxy(sdk);
      },
      port1,
    );
    (event.source as Window).postMessage(createReceivePortMessage(), '*', [
      port2,
    ]);
  }

  window.addEventListener('message', handleMessage);
  return {
    unsubscribe: () => window.removeEventListener('message', handleMessage),
  };
}

export async function onApiRequestedFromWorker(
  worker: Worker,
  onCreateSDKFactory: CreateSDKFactory,
) {
  function handleMessage(event: MessageEvent) {
    if (!isRequestPortMessage(event)) {
      return;
    }

    const sdkFactory = onCreateSDKFactory(
      event as MessageEvent<RequestApiEvent>,
    );

    if (!sdkFactory) {
      return;
    }

    const { port1, port2 } = new MessageChannel();
    Comlink.expose(
      // We need to wrap an instance of the SDK with a proxy object
      (...args: unknown[]) => {
        const sdk = sdkFactory(...args);
        return Comlink.proxy(sdk);
      },
      port1,
    );

    worker.postMessage(createReceivePortMessage(), [port2]);
  }

  worker.addEventListener('message', handleMessage);
  return {
    unsubscribe: () => worker.removeEventListener('message', handleMessage),
  };
}

export const wrap = Comlink.wrap;
